import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { emitSessionExpired, SessionExpiredReason, isJwtExpired } from '../utils/authEvents';

// Dynamic API base URL for development and production
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://links.usehypwave.com/api'  // Production URL
  : 'http://localhost:3001/api';        // Development URL

// API Service configured for both development and production

// Endpoints where a 401 should NOT trigger a global session-expired logout.
// A 401 on these routes means "wrong credentials / wrong key" and must be
// handled locally by the calling UI (login form, billing security gate, etc.).
const LOCAL_AUTH_401_ENDPOINTS = [
  '/auth/login-admin',
  '/auth/login-publisher',
  '/auth/login-client',
  '/auth/login-agency',
  '/auth/login-portal',
  '/auth/2fa',
  '/auth/verify-2fa',
  '/auth/verify-sensitive-password',
];

function isLocalAuth401Endpoint(url: string | undefined): boolean {
  if (!url) return false;
  return LOCAL_AUTH_401_ENDPOINTS.some((p) => url.includes(p));
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: false,
    });

    // Request interceptor: attach token, and short-circuit if it's already
    // expired so we don't even bother the server (avoids a flicker of
    // "loading → error → logout").
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          // Pre-empt obviously-expired tokens — except on the login flow
          // itself (which doesn't need a token).
          if (!isLocalAuth401Endpoint(config.url) && isJwtExpired(token)) {
            emitSessionExpired({
              reason: 'TOKEN_EXPIRED',
              message: 'Your session has expired. Please log in again.',
            });
            // Cancel this outgoing request — the listener will redirect.
            return Promise.reject({
              __sessionExpired: true,
              message: 'Session expired',
            });
          }
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: any 401 from a non-login endpoint means the
    // session is no longer valid. Clear state and redirect once.
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url: string | undefined = error?.config?.url;

        if (status === 401 && !isLocalAuth401Endpoint(url)) {
          const code: SessionExpiredReason =
            (error.response?.data?.code as SessionExpiredReason) || 'AUTH_FAILED';
          const message =
            code === 'TOKEN_EXPIRED'
              ? 'Your session has expired. Please log in again.'
              : code === 'USER_INACTIVE'
                ? 'Your account is no longer active. Please log in again.'
                : 'You have been signed out. Please log in again.';

          emitSessionExpired({ reason: code, message });
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, params?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: { timeout?: number }): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data, {
      timeout: config?.timeout,
    });
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data);
    return response.data;
  }

  async upload<T>(url: string, formData: FormData): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export const apiService = new ApiService();
